// Validation schema here (object mix from all necessary fields) for step01
import { z } from 'zod';

import { FieldTypes } from '@features/campaigns/builder/components/FormFields/FieldTypes.ts';
import { MIN_BONUS_IMPRESSIONS_VALUE } from '@features/campaigns/builder/consts/common.ts';
import { creativeFileValidationCheck } from '@features/campaigns/builder/utils/creativeFileValidationCheck.ts';
import {
  ALLOWED_CREATIVE_FORMATS,
  CREATIVE_FORMAT_IMAGE_PNG,
} from '@features/campaigns/builder/utils/isCreativeFormatValid.ts';
import { numberOfCreativesValidationCheck } from '@features/campaigns/builder/utils/numberOfCreativesValidationCheck.ts';
import {
  AgeGender,
  CampaignMediaType,
  CampaignStatus,
  CampaignType,
  GenericTargetCollectionItem,
  IncludeOperation,
  InterestSegments,
  Operation,
  Provinces,
} from '@features/campaigns/shared/types/campaign';
import { AccessRight } from '@shared/api/commonApiInterfaces';
import { CampaignId } from '@shared/types/campaign.ts';

const step01CommonValidationSchema = {
  [FieldTypes.BUDGET]: z
    .number({
      required_error: 'errorMessages.required',
      invalid_type_error: 'errorMessages.numericalValueRequired',
    })
    .min(1, 'errorMessages.minBudgetRequired')
    .max(5000000, 'errorMessages.maxBudgetRequired'),
  [FieldTypes.DATERANGE]: z
    .array(
      z.date({
        required_error: 'errorMessages.required',
        invalid_type_error: 'errorMessages.dateRangeProvideEndDate',
      }),
    )
    .length(2, 'errorMessages.dateRangeLength'),
  [FieldTypes.CAMPAIGN_NAME]: z
    .string({
      required_error: 'errorMessages.required',
    })
    .min(1, 'errorMessages.required')
    .refine((value) => value.trim().length > 0, 'errorMessages.atLeastOneCharacterRequired'),
};

export const step01validationSchema = z.object({
  ...step01CommonValidationSchema,
  [FieldTypes.ADVERTISER]: z
    .string({
      required_error: 'errorMessages.required',
    })
    .nonempty({
      message: 'errorMessages.required',
    }),
  [FieldTypes.BRAND]: z
    .string({
      required_error: 'errorMessages.required',
    })
    .nonempty({
      message: 'errorMessages.required',
    }),
  [FieldTypes.AGENCY_COMMISSION]: z
    .boolean({
      required_error: 'errorMessages.required',
    })
    .optional(),
  [FieldTypes.IS_NAME_AUTOGENERATED]: z.boolean(),
});

export enum TreeItemType {
  City = 'city',
  Province = 'province',
}

export type CreateCampaignFormStep01Values = z.infer<typeof step01validationSchema>;

export const step01ExternalValidationSchemaInitializationStep = z.object({
  ...step01CommonValidationSchema,
  [FieldTypes.ADVERTISER]: z.string().optional(),
  [FieldTypes.BILLING_CONTACT]: z.string().optional(),
});

export const step01ExternalValidationSchema = z.object({
  ...step01CommonValidationSchema,
  [FieldTypes.ADVERTISER]: z
    .string({
      required_error: 'errorMessages.required',
    })
    .nonempty({
      message: 'errorMessages.required',
    }),
  [FieldTypes.BILLING_CONTACT]: z
    .string({
      required_error: 'errorMessages.required',
    })
    .nonempty({
      message: 'errorMessages.required',
    }),
});

export type CreateCampaignFormStep01ExternalValues = z.infer<typeof step01ExternalValidationSchema>;
export type CreateCampaignFormStep01ExternalValuesOptionalFields = z.infer<
  typeof step01ExternalValidationSchemaInitializationStep
>;

export interface FormPageStepProps<T> {
  campaignId: CampaignId;
  campaignType: CampaignType;
  mediaType?: CampaignMediaType;
  stepSavedData: T;
  accessRights?: AccessRight[];
}

export interface RestoredStep01Data extends CreateCampaignFormStep01Values {
  id: CampaignId;
  advertiserName: string;
  advertiserCode: string;
  brandName: string;
  campaignType: CampaignType;
  brandCode: string;
  status: CampaignStatus;
  createdBy: string;
}

export interface RestoredStep01ExternalData extends CreateCampaignFormStep01ExternalValuesOptionalFields {
  id: CampaignId;
  advertiserName?: string;
  advertiserCode?: string;
  billingContactName?: string;
  billingContactCode?: string;
  status: CampaignStatus;
  createdBy: string;
}

export interface FormPageInitialStepProps {
  campaignId?: CampaignId;
  mediaType?: CampaignMediaType;
  stepSavedData?: RestoredStep01Data;
  accessRights?: AccessRight[];
}

export interface ProvincesTree {
  id: string;
  name: string;
  type: TreeItemType;
  label: string;
  cities: string[];
}

export interface CreateCampaignFormStep02Values {
  contentPackage: GenericTargetCollectionItem | null;
  provinces: Provinces | null;
  cities: Provinces | null;
  ageGender: AgeGender | null;
  interestSegments: InterestSegments | null;
  provincesOperationType: Operation | null;
  ageAndGenderOperationType: Operation | null;
  interestSegmentsOperationType: Operation | null;
  contentPackageOperationType: IncludeOperation | null;
}

export const step03validationSchema = z
  .object({
    [FieldTypes.FORMAT_TYPE_VIDEO]: z.boolean({}).optional(),
    [FieldTypes.FORMAT_TYPE_DISPLAY]: z.boolean({}).optional(),
    [FieldTypes.IS_CREATIVE_SERVICES_FULL]: z.boolean({}).optional(),
    [FieldTypes.NUMBER_OF_CREATIVES]: z
      .number({ invalid_type_error: 'errorMessages.numberOfCreativesWrongType' })
      .optional(),
    [FieldTypes.VIDEO_PRODUCTS_LINEAR_ADDRESSABLE]: z.boolean({}).optional(),
    [FieldTypes.CREATIVE_L_SHAPED_IMAGE]: z
      .any()
      .superRefine(async (value, ctx) => creativeFileValidationCheck(value, ctx, [CREATIVE_FORMAT_IMAGE_PNG])),
    [FieldTypes.CREATIVE_LANDING_IMAGE]: z
      .any()
      .superRefine(async (value, ctx) => creativeFileValidationCheck(value, ctx, ALLOWED_CREATIVE_FORMATS)),
  })
  .superRefine(({ formatTypeDisplay, isCreativeServicesFull, numberOfCreatives }, ctx) =>
    numberOfCreativesValidationCheck({ formatTypeDisplay, isCreativeServicesFull, numberOfCreatives }, ctx),
  );

export type UpdateCampaignFormStep03Values = z.infer<typeof step03validationSchema>;

export const step04validationSchema = z
  .object({
    [FieldTypes.IMPRESSIONS]: z.number({ invalid_type_error: 'errorMessages.numericalValueRequired' }).optional(),
    [FieldTypes.BONUS_IMPRESSIONS_TOGGLE]: z.boolean().optional(),
    [FieldTypes.BONUS_IMPRESSIONS]: z
      .number({ invalid_type_error: 'errorMessages.numericalValueRequired' })
      .min(MIN_BONUS_IMPRESSIONS_VALUE, 'errorMessages.minBonusImpressionsValue')
      .nullable()
      .optional(),
    [FieldTypes.CPM]: z.number({ invalid_type_error: 'errorMessages.numericalValueRequired' }).optional(),
    [FieldTypes.ADDITIONAL_NOTE]: z.string().max(5000, 'errorMessages.maxAdditionalNoteLength').optional(),
    [FieldTypes.BUDGET]: z
      .number({
        required_error: 'errorMessages.required',
        invalid_type_error: 'errorMessages.numericalValueRequired',
      })
      .optional(),
  })
  .superRefine(({ bonusImpressionsToggle, bonusImpressions }, ctx) => {
    if (bonusImpressionsToggle && bonusImpressions === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorMessages.numericalValueRequired',
        path: [FieldTypes.BONUS_IMPRESSIONS],
      });
    }
  });

export type UpdateCampaignFormStep04Values = z.infer<typeof step04validationSchema>;
